import { Button, Drawer, Stack } from '@mui/material'
import { FC, useState } from 'react'
import { Fade, IconButton, Icons, InformationModal } from 'utoniq-ui'

import { ItemThumbnailType } from '@/clients/utoniq-core/schema'
import { ItemReversibleThumbnail } from '@/components/organisms/item/ItemReversableThumbnail'
import { VoicePlayer } from '@/components/organisms/resourcePlayer/VoicePlayer'
import { styles } from '@/components/styles'
import { useMessage, useRouter } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { useTranslation } from '@/core/i18n'
import { isNativePlatform } from '@/core/native/app'

import type { State } from '../../../viewModel'
import { ConfirmCustomProfileSettingModal } from '../ConfirmCustomProfileSettingModal'
import { ItemDescriptionCell } from '../ItemDescription'
import { SupplementLinkSetCell } from '../SupplementLinkSet'
import { ThumbnailActionAreaCell } from '../ThumbnailActionArea'
import { KonbiniPrintModal } from './KonbiniPrintModal'

export type Props = {
  loading: boolean
  QRCodePrinting: boolean
  portfolio: State['portfolio']
  user: State['user']
  customProfileUpdating: boolean
  isFlipped: boolean
  onReverseThumbnail: () => void
  onClickProtectUserItem: () => void
  onSetWallpaper: () => Promise<boolean>
  onOpenFullScreen?: () => void
  onOpenMultipleImage?: () => void
  onImageDownload: (url?: string) => Promise<void>
  onGenerateWallpaperPrintQrcode: (serialId: string) => Promise<void>
}

export const OwnedItemDetailWallpaperView: Organism<Props> = ({
  loading,
  QRCodePrinting,
  portfolio,
  user,
  isFlipped,
  customProfileUpdating,
  onReverseThumbnail,
  onClickProtectUserItem,
  onSetWallpaper,
  onOpenFullScreen,
  onOpenMultipleImage,
  onImageDownload,
  onGenerateWallpaperPrintQrcode,
}) => {
  const audioResource = portfolio?.item?.wallpaperResources?.audioResource
  const { showError, showMessage } = useMessage()
  const { router, routerPages } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const [downloading, setDownloading] = useState<boolean>(false)
  const [changing, setChanging] = useState<boolean>(false)

  const [openImageDownloadConfirm, setOpenImageDownloadConfirm] =
    useState(false)
  const [openCustomProfileModal, setOpenCustomProfileModal] = useState(false)
  const [unset, setUnset] = useState<boolean>(
    !!user?.customProfile?.wallpaperItem &&
      portfolio?.item?.id === user?.customProfile?.wallpaperItem?.id
  )
  const { t } = useTranslation('ownedItemDetail')

  const handleDownloadImage = async (url?: string) => {
    setDownloading(true)
    await onImageDownload(url)
      .then(() => {
        isNativePlatform() && setOpenImageDownloadConfirm(true)
        setDownloading(false)
      })
      .catch(() => setDownloading(false))
  }

  const handleSetWallpaper = async () => {
    setChanging(true)
    await onSetWallpaper()
      .then(unsetWallpaper => {
        setUnset(unsetWallpaper)
        showMessage(
          unsetWallpaper ? t('setBackground') : t('cancelBackground'),
          'info',
          <Button
            variant="text"
            size="small"
            sx={{ color: 'white' }}
            loading={loading}
            onClick={() => router.push(routerPages.MyProfile)}
          >
            {t('seeProfile')}
          </Button>
        )
      })
      .catch(() => showError(t('noSettingConfirmContent')))
      .finally(() => {
        setOpenCustomProfileModal(false)
        setChanging(false)
      })
  }

  return (
    <>
      {/* 背景設定時に出るモーダル */}
      <ConfirmCustomProfileSettingModal
        open={openCustomProfileModal}
        onClose={() => setOpenCustomProfileModal(false)}
        onClick={() => handleSetWallpaper()}
        loading={changing}
        unset={unset}
        type={portfolio?.item?.type}
      />
      {/* 画像ダウンロード後に出るモーダル */}
      <InformationModal
        open={openImageDownloadConfirm}
        onClose={() => setOpenImageDownloadConfirm(false)}
        content={null}
        title={t('wallpaperDownloaded')}
        buttons={
          <Button
            sx={{ width: 212 }}
            size="large"
            onClick={() => setOpenImageDownloadConfirm(false)}
          >
            {t('ok')}
          </Button>
        }
      />
      <PrinterBottomSheet
        open={open}
        QRCodePrinting={QRCodePrinting}
        onClose={() => setOpen(false)}
        portfolio={portfolio}
        onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
      />

      <CellFrame
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 0 auto',
        }}
      >
        <Stack
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          flex={1}
        >
          <Stack
            alignItems="center"
            sx={{
              position: 'relative',
              '.MuiButtonBase-root': {
                height: 'fit-content',
              },
            }}
          >
            <ThumbnailActionAreaCell
              onOpenFullScreen={onOpenFullScreen}
              onOpenMultipleImage={onOpenMultipleImage}
              numberImage={portfolio?.item?.images?.length ?? 0}
            />
            <ItemReversibleThumbnail
              loading={loading}
              flipProps={{
                isFlipped,
              }}
              imageAreaProps={{
                thumbnail: portfolio?.item?.thumbnail,
                size:
                  portfolio?.item.thumbnailType === ItemThumbnailType.Square
                    ? 'x-large-square'
                    : 'x-large',
                onClick: onReverseThumbnail,
              }}
            />
          </Stack>
          <ItemDescriptionCell portfolio={portfolio} />
          <Stack sx={{ mt: 4, mb: 2 }} direction="row" spacing={6}>
            {audioResource ? (
              <VoicePlayer
                resource={audioResource}
                bgcolor="primary.main"
                color="white"
              />
            ) : (
              <IconButton.Rotate
                size="small"
                onClick={onReverseThumbnail}
                bgcolor="primary.main"
                color="white"
              />
            )}
            <IconButton.Background
              size="small"
              onClick={() => setOpenCustomProfileModal(true)}
              bgcolor={unset ? 'grey.100' : 'primary.main'}
              color={unset ? 'grey.300' : 'white'}
            />
          </Stack>
          <Stack sx={{ my: 2 }} spacing={4} direction="row">
            {portfolio?.item?.metadata?.wallpaper?.isPrintEnabled && (
              <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                startIcon={<Icons.Printer sx={{ color: 'grey.700' }} />}
              >
                {t('storePrint', 'コンビニプリント')}
              </Button>
            )}
            {portfolio?.item?.isDownloadable && (
              <Button
                variant="outlined"
                onClick={() =>
                  handleDownloadImage(
                    portfolio?.item?.wallpaperResources?.wallpaperResource?.uri
                  )
                }
                loading={downloading}
                startIcon={<Icons.Download sx={{ color: 'grey.700' }} />}
              >
                {t('downloadWallpaper')}
              </Button>
            )}
          </Stack>
        </Stack>

        <SupplementLinkSetCell
          sku={portfolio?.item.sku}
          itemId={portfolio?.item.id}
          commentCount={portfolio?.item.commentCount}
          relatedMissions={portfolio?.item.relatedMissions}
          artistId={portfolio?.item.issuedArtist?.id ?? ''}
          isUserProtected={portfolio?.isUserProtected}
          issuableNft={!!portfolio?.item.nft?.chains}
          onClickProtectUserItem={onClickProtectUserItem}
        />
      </CellFrame>
    </>
  )
}

type PrinterBottomSheetProps = {
  open: boolean
  QRCodePrinting: boolean
  onClose: () => void
  portfolio: State['portfolio']
  onGenerateWallpaperPrintQrcode: Props['onGenerateWallpaperPrintQrcode']
}
const PrinterBottomSheet: FC<PrinterBottomSheetProps> = ({
  QRCodePrinting,
  open,
  onClose,
  portfolio,
  onGenerateWallpaperPrintQrcode,
}) => {
  return (
    <Drawer open={open} onClose={onClose} sx={{ zIndex: 1300 }}>
      <Stack
        height={`calc(95dvh - ${styles.safeAreaTop})`}
        sx={{ bgcolor: 'background.default' }}
      >
        <Fade>
          <KonbiniPrintModal
            QRCodePrinting={QRCodePrinting}
            onClose={onClose}
            portfolio={portfolio}
            onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
          />
        </Fade>
      </Stack>
    </Drawer>
  )
}
